// Lib
import { escape, isEmpty } from 'lodash/fp';

import randomString from '../../../../common/uid/randomString';
import { asRect } from '../../../../common/maths/geometry/rect';
import { asObject } from '../../../../common/utils/immutableHelper';
import { canvasElementSorter } from '../../../../common/elements/utils/elementSortUtils';
import { getBoundingRectInGridUnits } from '../../../canvas/dnd/canvasUtil';
import { isCommentThread, isImage } from '../../../../common/elements/utils/elementTypeUtils';
import getElementClipboardHtmlString from '../utils/getElementClipboardHtmlString';
import { saveToClipboard } from './clipboardActions';
import { ClipboardOperation, ELEMENTS_PAYLOAD_ATTRIBUTE } from './clipboardConstants';
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import { DataTransferTypes, MimeType } from '../../../../common/media/mimeConstants';
import getGridSize from '../../../utils/grid/gridSizeSelector';
import { getMeasurementsMap } from '../../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { getSelectedElements } from '../../../element/selection/selectedElementsSelector';
import { getCurrentlyEditingId } from '../../../element/selection/currentlyEditingSelector';
import { getCurrentModalElementId } from '../../../element/modal/elementModalCommonSelector';
import { getPageIdSelector } from '../../../reducers/sessionSelector';
import { Thunk } from '../../../types/reduxTypes';
import { getCurrentUser, ImMNCurrentUser } from '../../../user/currentUserSelector';
import { isStaff } from '../../../user/userUtil';
import getElementContentsString from '../utils/getElementContentsString';
import globalLogger from '../../../logger';
import { getElementImagePngBlob } from '../utils/getElementImageBlob';
import { getIsFeatureEnabledForCurrentUser } from '../../../element/feature/elementFeatureSelector';
import { isFeatureEnabled } from '../../../../common/users/utils/userPropertyUtils';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';
import type { PlatformDetails } from '../../../../common/platform/platformTypes';
import { writeToNativeClipboard } from '../../../capacitor/utils/capacitorClipboardUtils';
import { isPlatformModernMobileApp } from '../../../platform/utils/platformDetailsUtils';
import { normaliseClipboardText } from '../../../../common/tiptap/extensions/clipper/ClipboardCopyHandler';

const logger = globalLogger.createChannel('clipboardUtils');

/** Checks whether text can be written from a context-menu action on this platform. */
export const isTextClipboardWriteSupported = (platformDetails: PlatformDetails): boolean =>
    isPlatformModernMobileApp(platformDetails) ||
    (typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function');

/** Writes text through the native bridge or browser Clipboard API when available. */
export const writeTextToClipboard = async (text: string, platformDetails: PlatformDetails): Promise<void> => {
    if (isPlatformModernMobileApp(platformDetails)) return writeToNativeClipboard(text);
    if (typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function') return;

    await navigator.clipboard.writeText(text);
};

const CLIPBOARD_HTML_META = "<meta charSet='utf-8'>";

/** Each element's HTML representation, separated by an empty div. */
const getElementsClipboardHtml = (elements: MNElement[]): string | undefined => {
    const htmlStr = elements
        .map(getElementClipboardHtmlString)
        .filter(Boolean)
        .map((elementHtml) => `${elementHtml}<div></div>`)
        .join('');

    return isEmpty(htmlStr) ? undefined : `${CLIPBOARD_HTML_META}${htmlStr}`;
};

/** Each element's text content on its own line. */
const getElementsClipboardText = (elements: MNElement[]): string | undefined => {
    const textStr = normaliseClipboardText(elements.map(getElementContentsString).filter(Boolean).join('\n'));
    return isEmpty(textStr) ? undefined : textStr;
};

/** An empty element carrying the payload, so other programs render nothing extra and Milanote can paste the elements. */
const getElementsPayloadHtml = (elementsDataString: string): string =>
    `<div ${ELEMENTS_PAYLOAD_ATTRIBUTE}="${escape(elementsDataString)}"></div>`;

type WriteElementImageToClipboardProps = {
    element: MNElement;
    elementsDataString: string;
    htmlStr?: string;
    textStr?: string;
    renderSecureImagesThroughCF: boolean;
};

/**
 * Replaces the clipboard with one item carrying the image pixels alongside the representations already
 * written synchronously to the copy event. The PNG is a promise so the write is issued inside the user
 * gesture (Safari requires it) and the download never delays the copy; until it resolves, or if it
 * fails, the clipboard keeps the synchronous data.
 */
const writeElementImageToClipboard = ({
    element,
    elementsDataString,
    htmlStr,
    textStr,
    renderSecureImagesThroughCF,
}: WriteElementImageToClipboardProps): Promise<void> => {
    const items: Record<string, Blob | Promise<Blob>> = {
        [MimeType.PNG]: getElementImagePngBlob(element, renderSecureImagesThroughCF),
        [DataTransferTypes.HTML]: new Blob(
            [`${htmlStr ?? CLIPBOARD_HTML_META}${getElementsPayloadHtml(elementsDataString)}`],
            {
                type: DataTransferTypes.HTML,
            },
        ),
    };
    if (textStr) items[DataTransferTypes.PLAIN_TEXT] = new Blob([textStr], { type: DataTransferTypes.PLAIN_TEXT });

    return navigator.clipboard.write([new ClipboardItem(items)]).catch((error) => {
        logger.error('Failed to write image to clipboard', error);
    });
};

type AddElementDataToClipboardProps = {
    event: ClipboardEvent;
    saveId: string;
    operation: ClipboardOperation;
    sessionId: string;
    elements: MNElement[];
    elementsBoundingRect: Rect;
    user: ImMNCurrentUser;
    renderSecureImagesThroughCF: boolean;
};

/** Whether this browser can write image data through the async Clipboard API. */
const supportsNativeImageCopy = (): boolean =>
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function' &&
    typeof window.ClipboardItem === 'function';

/** The clipboard holds one image, so only a lone image element gets its pixels written. */
const isSelectionCopyableImage = (elements: MNElement[]): boolean =>
    supportsNativeImageCopy() && elements.length === 1 && isImage(elements[0]);

export const addElementDataToClipboard = ({
    event,
    saveId,
    operation,
    sessionId,
    elements,
    elementsBoundingRect,
    user,
    renderSecureImagesThroughCF,
}: AddElementDataToClipboardProps): Promise<void> | undefined => {
    // Our application "elements" data type
    const dataString = JSON.stringify({ saveId, operation, sessionId, elements, elementsBoundingRect });
    event.clipboardData?.setData(DataTransferTypes.ELEMENTS, dataString);

    // HTML and text for other programs
    const htmlStr = getElementsClipboardHtml(elements);
    if (htmlStr) event.clipboardData?.setData(DataTransferTypes.HTML, htmlStr);

    const textStr = getElementsClipboardText(elements);
    if (textStr) event.clipboardData?.setData(DataTransferTypes.PLAIN_TEXT, textStr);

    // A single image also gets its pixels written, so it can be pasted as an image into other programs
    // NOTE: This functionality is currently experimental and behind a feature flag
    if (!isFeatureEnabled(ExperimentId.copyImageData, user) || !isSelectionCopyableImage(elements)) return;

    return writeElementImageToClipboard({
        element: elements[0],
        elementsDataString: dataString,
        htmlStr,
        textStr,
        renderSecureImagesThroughCF,
    });
};

export const handleSaveToClipboardEventThunk =
    (event: ClipboardEvent, operation: ClipboardOperation): Thunk =>
    (dispatch, getState, { measurementsStore }) => {
        const state = getState();
        const gridSize = getGridSize(state);

        const selectedElements = getSelectedElements(state);
        const currentlyEditingId = getCurrentlyEditingId(state);
        const currentModalElementId = getCurrentModalElementId(state);

        if (!selectedElements.size || currentlyEditingId || currentModalElementId) return;

        // If only selecting a comment, let the user highlight and select comment text,
        // but only if they have actually selected text within the comment.
        // If no text is selected (selection is collapsed), proceed with the element clipboard operation.
        if (selectedElements.size === 1 && isCommentThread(selectedElements.first())) {
            const selection = window.getSelection();
            // If there's selected text (not collapsed), let the native copy/cut happen
            if (selection && !selection.isCollapsed) return;
        }

        event.preventDefault();

        const measurements = getMeasurementsMap(measurementsStore.getState());

        const saveId = randomString(16);
        const sessionId = getPageIdSelector(state);
        const elements = asObject(selectedElements.sort(canvasElementSorter)) as MNElement[];
        const elementsBoundingRect = asRect(
            getBoundingRectInGridUnits({
                elements,
                measurements,
                gridSize,
            }),
        );

        // Annotate copied data with measurements. This is just for exporting to the marketing site
        // animation tool and is meaningless otherwise, so only do this if the experiment is enabled.
        const user = getCurrentUser(state);
        if (isStaff(user)) {
            (elements as (MNElement & { measurements: Rect })[]).forEach((element) => {
                element.measurements = asObject(measurements.get(element.id!));
            });
        }

        const renderSecureImagesThroughCF = getIsFeatureEnabledForCurrentUser(
            ExperimentId.renderSecureImagesThroughCF,
            true,
        )(state);

        addElementDataToClipboard({
            event,
            saveId,
            elements,
            elementsBoundingRect,
            operation,
            sessionId,
            user,
            renderSecureImagesThroughCF,
        });

        // On paste start offset at original location (index of -1)
        const pasteCount = operation === ClipboardOperation.cut ? -1 : 0;
        dispatch(saveToClipboard({ operation, elements, saveId, pasteCount, elementsBoundingRect }));
    };
